// Sustain-aware held-key model. `matching` is the set the engine compares against
// each frame: physically-held pitches plus pitches released while the sustain
// pedal (CC64) is down. Pure — no DOM, no Web MIDI — so it is unit-testable.
// Methods return whether `matching` changed, so callers can gate change callbacks.

export class HeldKeys {
  /** Pitches the engine matches against (held ∪ sustained). */
  readonly matching = new Set<number>();
  private readonly down = new Set<number>();
  private readonly sustained = new Set<number>();
  private readonly velocity = new Map<number, number>();
  private pedal = false;

  /** Onset velocity (0..1) of the most recent press of `pitch`; 1 if unknown. */
  velocityOf(pitch: number): number {
    return this.velocity.get(pitch) ?? 1;
  }

  noteOn(pitch: number, velocity = 1): boolean {
    this.velocity.set(pitch, velocity);
    this.down.add(pitch);
    this.sustained.delete(pitch);
    if (this.matching.has(pitch)) return false;
    this.matching.add(pitch);
    return true;
  }

  noteOff(pitch: number): boolean {
    this.down.delete(pitch);
    if (this.pedal) {
      if (this.matching.has(pitch)) this.sustained.add(pitch);
      return false;
    }
    this.sustained.delete(pitch);
    if (!this.matching.has(pitch)) return false;
    this.matching.delete(pitch);
    return true;
  }

  setPedal(down: boolean): boolean {
    if (down === this.pedal) return false;
    this.pedal = down;
    if (down) return false;
    let changed = false;
    for (const pitch of this.sustained) {
      if (!this.down.has(pitch)) {
        this.matching.delete(pitch);
        changed = true;
      }
    }
    this.sustained.clear();
    return changed;
  }

  clear(): boolean {
    const had = this.matching.size > 0;
    this.down.clear();
    this.sustained.clear();
    this.matching.clear();
    this.velocity.clear();
    this.pedal = false;
    return had;
  }
}
